import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@material-ui/core/Dialog';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { MuiTooltipV2 } from 'src/components/MuiTooltip/MuiTooltip';
import PassengersAddressesMatrix from './index.PassengersAddressesMatrix';
import PassengersAddressesTour from './tour.PassengersAddressesMatrix';
import * as TourStyles from './styles.PassengersAddressesTour';
import helpers, { AddressChoices } from './helpers.PassengersAddressesMatrix';
import hooks from './hooks.PassengersAddressesMatrix';
import * as Styles from './styles.PassengersAddressesPicker';
import * as MatrixStyles from './styles.PassengersAddressesMatrix';
import { StepNumber } from '../StepNumber/styles.StepNumber';

interface Passenger {
    passId: string;
    fullName: string;
    departmentName?: string;
}

interface Props {
    passengers: Passenger[];
    choices: AddressChoices;
    onChoicesChange: (choices: AddressChoices) => void;
}

/**
 * The per-passenger addresses, for several passengers placed together. In the placement panel a summary and
 * a button; the matrix itself opens in a large dialog, because on a 1366x768 screen the panel left it room for
 * one or two passengers (owner, 2026-11-01). The dialog edits a draft: "save" applies it, "cancel" drops it.
 */
const PassengersAddressesPicker = ({ passengers, choices, onChoicesChange }: Props) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [draft, setDraft] = useState<AddressChoices>(choices);
    const [isTourOpen, setIsTourOpen] = useState(false);
    const dialogBodyRef = useRef<HTMLDivElement>(null);

    const passIds = useMemo(() => passengers.map((p) => p.passId), [passengers]);
    // Fetched here, where it outlives the dialog: once per passenger, ready before the dialog opens
    const addressesData = hooks.usePassengersAddresses(passIds);

    const { changed, defaults } = helpers.countChosen(passIds, choices);
    const draftCount = helpers.countChosen(passIds, draft);

    const open = (withTour = false) => {
        setDraft(choices);
        setIsTourOpen(withTour);
        setIsOpen(true);
    };
    const cancel = () => {
        setIsTourOpen(false);
        setIsOpen(false);
    };
    const closeTour = () => setIsTourOpen(false);

    // Opens the tour only when clicked: it never starts by itself (owner, 2026-11-01)
    const guideButton = (onClick: () => void) => (
        <MuiTooltipV2 arrow title={isTourOpen ? '' : (t('addressesTourTooltip') as string)}>
            <TourStyles.GuideButton type="button" onClick={onClick} aria-label={t('addressesTourTooltip')}>
                <HelpOutlineIcon aria-hidden="true" />
                {t('addressesTourButton')}
            </TourStyles.GuideButton>
        </MuiTooltipV2>
    );
    // A click beside the dialog does not throw away a whole matrix of edits: Escape and Cancel do
    const onDialogClose = (_: unknown, reason?: string) => {
        if (reason === 'backdropClick') return;
        // Escape during the tour ends the tour (focus may be in the table after a click there), never the edits
        if (reason === 'escapeKeyDown' && isTourOpen) {
            setIsTourOpen(false);
            return;
        }
        cancel();
    };
    const save = () => {
        onChoicesChange(draft);
        setIsTourOpen(false);
        setIsOpen(false);
    };

    return (
        <Styles.Summary aria-labelledby="passengers-addresses-summary-title">
            <Styles.SummaryTitle id="passengers-addresses-summary-title">
                <StepNumber>2</StepNumber>
                {t('addressesMatrixTitle')}
            </Styles.SummaryTitle>
            <Styles.SummaryText role="status">{t('addressesPickerSummary', { changed, defaults })}</Styles.SummaryText>
            <Styles.OpenRow>
                <Styles.OpenButton type="button" onClick={() => open()}>
                    {t('addressesPickerOpen')}
                </Styles.OpenButton>
                {guideButton(() => open(true))}
            </Styles.OpenRow>

            <Dialog
                open={isOpen}
                onClose={onDialogClose}
                fullWidth
                maxWidth="lg"
                aria-labelledby="passengers-addresses-dialog-title"
                PaperProps={{ style: { height: 'calc(100% - 64px)' } }}
            >
                <Styles.DialogBody dir="rtl" ref={dialogBodyRef}>
                    {isTourOpen && <PassengersAddressesTour containerRef={dialogBodyRef} onClose={closeTour} />}
                    <Styles.DialogHeader>
                        <div data-tour="hint">
                            <Styles.DialogTitle id="passengers-addresses-dialog-title">
                                {t('addressesPickerTitle', { count: passengers.length })}
                            </Styles.DialogTitle>
                            <Styles.DialogHint>{t('addressesPickerHint')}</Styles.DialogHint>
                            <Styles.DialogHint>{t('addressesPickerHintRules')}</Styles.DialogHint>
                        </div>
                        <Styles.HeaderSide>
                            <MatrixStyles.DialogLegend>
                                <MatrixStyles.DialogLegendItem $kind="pickup">{t('pickup')}</MatrixStyles.DialogLegendItem>
                                <MatrixStyles.DialogLegendItem $kind="drop">{t('drop')}</MatrixStyles.DialogLegendItem>
                            </MatrixStyles.DialogLegend>
                            {guideButton(() => setIsTourOpen(true))}
                        </Styles.HeaderSide>
                    </Styles.DialogHeader>
                    <Styles.DialogContent>
                        <PassengersAddressesMatrix
                            passengers={passengers}
                            choices={draft}
                            onChoicesChange={setDraft}
                            layout="dialog"
                            addressesData={addressesData}
                        />
                    </Styles.DialogContent>
                    <Styles.DialogActions data-tour="footer">
                        {/* The draft's count, so the effect of a click shows before saving */}
                        <Styles.DialogSummary aria-live="polite">
                            {t('addressesPickerSummary', draftCount)}
                        </Styles.DialogSummary>
                        <Styles.DialogButtons>
                            <Styles.CancelButton type="button" onClick={cancel}>
                                {t('cancel')}
                            </Styles.CancelButton>
                            <Styles.SaveButton type="button" onClick={save}>
                                {t('addressesPickerSave')}
                            </Styles.SaveButton>
                        </Styles.DialogButtons>
                    </Styles.DialogActions>
                </Styles.DialogBody>
            </Dialog>
        </Styles.Summary>
    );
};

export default PassengersAddressesPicker;
